import * as React from 'react';
import { styles } from './styles';
import { DemoSection, DemoSectionProps } from '../components/DemoSection';
import { ThemeDesignConfig, ThemeDesignHierarchy, StyleBucket } from '../../../types';
import { ChevronDown } from '@routes/event/create/components/ChevronDown';
import { SelectableOption } from '../../SelectableOption';
import { BaseButton } from '@routes/event/create/components/BaseButton';
import { DesignDemo_designShowcase_organization_allStyles } from '@graphql/designShowcaseTypes';

const Option: React.SFC<{
  onClick: React.MouseEventHandler<HTMLElement>;
  value: string;
  children: JSX.Element;
  type: ThemeDesignHierarchy;
  className?: string;
  label?: string;
}> = ({ onClick, value, type, children, className, label }) => {
  return (
    <SelectableOption
      onClick={onClick}
      section="themes"
      value={value}
      data-type={type}
      data-section="themes"
      className={className}
      aria-label={label}
      title={label}
    >
      {children}
    </SelectableOption>
  );
};

const renderThemeCategories = (
  category: Props['category'],
  styleBuckets: Props['styleBuckets'],
  onSelect: Props['onSelect']
) => {
  return (
    <div className={styles.container}>
      {styleBuckets.map(styleBucket => {
        return (
          <Option
            value={styleBucket.organizationBucket.bucketId}
            key={styleBucket.organizationBucket.bucketId}
            onClick={onSelect}
            type="bucketGroup"
            className={styles.categoryRow(category === styleBucket.organizationBucket.bucketId)}
          >
            <div className={styles.categoryOptionContainer}>
              <div>{styleBucket.organizationBucket.displayString}</div>
              <ChevronDown className={styles.categoryOptionChevron} />
            </div>
          </Option>
        );
      })}
    </div>
  );
};
const renderThemeOptions = (
  currentTheme: Props['theme'],
  onThemeSelect: Props['onSelect'],
  bucketStyles: StyleBucket['styles']
) => {
  return (
    <>
      <div className={styles.themeContainer}>
        {bucketStyles.map(style => {
          const isBlank = style.id === 'blank';
          if (style.thumbnail || isBlank) {
            const isCurrent = currentTheme === style.id;
            return (
              <Option
                key={style.id}
                className={styles.themeButton}
                type="bucket"
                value={style.id}
                onClick={onThemeSelect}
                label={`Select '${style.name}' theme`}
              >
                <>
                  <div className={styles.themeOption(style.thumbnail ? style.thumbnail.url : null, isBlank)} />
                  {isCurrent && <div className={styles.currentBadge}>CURRENT</div>}
                </>
              </Option>
            );
          }

          return null;
        })}
      </div>
    </>
  );
};

interface Props extends DemoSectionProps {
  category: string | null;
  theme: string;
  selectedStyle: DesignDemo_designShowcase_organization_allStyles;
  showCategoryBuckets: ThemeDesignConfig['showCategoryBuckets'];
  styleBuckets: StyleBucket[];
  onSelect: React.MouseEventHandler<HTMLElement>;
  onBackClick: React.MouseEventHandler<HTMLElement>;
}

export class ThemeDesign extends React.PureComponent<Props> {
  getStyleBucketById = (category: Props['category'], styleBuckets: Props['styleBuckets']) => {
    return styleBuckets.find(styleBucket => styleBucket.organizationBucket.bucketId === category);
  };
  render(): React.ReactNode {
    const {
      open,
      showCategoryBuckets,
      category,
      theme,
      onSelect,
      onSectionToggle,
      onBackClick,
      styleBuckets,
      selectedStyle
    } = this.props;

    let content;

    let styleBucket = !!category ? this.getStyleBucketById(category, styleBuckets) : null;

    if (showCategoryBuckets && styleBucket) {
      content = renderThemeOptions(theme, onSelect, styleBucket.styles);
    } else {
      content = renderThemeCategories(category, styleBuckets, onSelect);
    }

    return (
      <DemoSection
        open={open}
        onSectionToggle={onSectionToggle}
        section="themes"
        name="Change Page Theme"
        subtitle={selectedStyle.name}
      >
        {showCategoryBuckets && styleBucket && (
          <BaseButton className={styles.selectedCategoryContainer} onClick={onBackClick}>
            <div className={styles.categoryChevron}>
              <ChevronDown />
            </div>
            <div className={styles.selectedCategory}>{styleBucket.organizationBucket.displayString}</div>
          </BaseButton>
        )}
        {open && content}
      </DemoSection>
    );
  }
}
